import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { getTreatments, getReviews } from "@/lib/api";
import TreatmentRecommender from "@/components/TreatmentRecommender";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_skin-stillness/artifacts/olfepwyf_WillSalkeld_Amarna_April26_75.jpg";
const STILLNESS_IMG = "/images/studio-room.jpg";
const FOUNDER_IMG = "/images/anissa-portrait.jpg";
const WELCOME_IMG =
  "https://customer-assets.emergentagent.com/job_skin-stillness/artifacts/608pbdf0_WillSalkeld_Amarna_April26_267.jpg";

const TESTIMONIALS = [
  {
    quote: "I cried before she'd even touched my face — and that was allowed. I left feeling lighter than I had in months.",
    name: "Lara",
    detail: "Bondi, 38",
  },
  {
    quote: "Anissa treated my rosacea as a whole-body conversation. For the first time, my skin felt understood — and so did I.",
    name: "Eve",
    detail: "Bondi, 44",
  },
  {
    quote: "I genuinely exhale before she's even begun. My shoulders drop the moment I close the door behind me.",
    name: "Marion",
    detail: "Bondi, 52",
  },
];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease: "easeOut", delay }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function Home() {
  const [treatments, setTreatments] = useState([]);
  const [liveReviews, setLiveReviews] = useState(null);
  useEffect(() => { getTreatments().then(setTreatments).catch(() => {}); }, []);
  useEffect(() => {
    getReviews().then((d) => { if (d?.configured && d.reviews?.length) setLiveReviews(d); }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO — The Material Study */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          {/* Very faint bottom warmth — barely there */}
          <div className="absolute inset-x-0 bottom-0 h-[40%] bg-gradient-to-t from-[#F7F2E8]/55 via-[#F7F2E8]/15 to-transparent" />
        </div>

        <div
          className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 w-full pt-[120px] lg:pt-[150px]"
          style={{ paddingBottom: "clamp(72px, 9vh, 130px)" }}
        >
          {/* Headline — appears slowly */}
          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            className="max-w-2xl"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.5, delay: 0.6 }}
              className="text-[11px] uppercase tracking-[0.32em] text-[#2F2C28] font-medium"
            >
              A private women's studio · By appointment
            </motion.div>

            <h1
              data-testid="hero-headline"
              className="serif mt-8 text-[36px] sm:text-[56px] lg:text-[72px] leading-[1.1] tracking-tight text-[#1F1D1B]"
              style={{ fontWeight: 300 }}
            >
              A private space where <span className="italic">skin, stillness</span> and nervous system care come together.
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.6, ease: "easeOut", delay: 1.6 }}
              className="mt-10 sm:mt-14"
            >
              <p className="text-[15px] sm:text-[17px] leading-[1.85] text-[#2E2B27] max-w-xl">
                Studio A is a private women's studio in Bondi Beach where personalised skincare, therapeutic touch and nervous system regulation come together. Every appointment is designed to help you slow down, reconnect and leave feeling lighter than when you arrived.
              </p>

              {/* Quiet text-link CTA — no pill */}
              <Link
                to="/book"
                data-testid="hero-book-btn"
                className="group mt-12 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-[#1F1D1B] font-medium border-b border-[#1F1D1B]/40 pb-2 hover:border-[#1F1D1B] transition-colors"
              >
                Begin Your Ritual
                <ArrowRight size={14} strokeWidth={1.4} className="transition-transform duration-500 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* WELCOME FROM ANISSA */}
      <section className="py-32 lg:py-44">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-24 items-center">
          <Reveal className="lg:col-span-5 img-zoom order-2 lg:order-1">
            <img src={WELCOME_IMG} alt="" className="w-full aspect-[4/5] object-cover" />
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 order-1 lg:order-2">
            <div className="label-eyebrow">A welcome from Anissa</div>
            <p className="serif mt-10 text-[28px] sm:text-[36px] lg:text-[44px] leading-[1.15] text-[#2E2B27] max-w-2xl">
              I made this space for the woman who walks in carrying a lot, and walks out a little lighter. There's no rush here. You don't need to be anyone but yourself.
            </p>
            <p className="serif italic text-[20px] mt-10 text-[#55524C]">— Anissa</p>
          </Reveal>
        </div>
      </section>

      {/* THE STUDIO A EXPERIENCE */}
      <section className="bg-[#EAE7E0]/60 py-32 lg:py-44">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 text-center">
          <Reveal>
            <div className="label-eyebrow">The Studio A Experience</div>
            <h2 className="serif mt-8 text-[36px] sm:text-[52px] lg:text-[64px] leading-[1.05] max-w-3xl mx-auto">
              More than a facial.
              <br />
              <span className="italic">A moment to reconnect.</span>
            </h2>
            <p className="mt-10 max-w-2xl mx-auto text-[18px] leading-[1.8] text-[#2E2B27]">
              A visit here is time intentionally set aside — to slow down, to be properly listened to, to understand your skin a little better, and to leave feeling calmer than when you arrived.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHY STUDIO A */}
      <section className="py-32 lg:py-44">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="text-center">
              <div className="label-eyebrow">Why Studio A</div>
              <h2 className="serif mt-6 text-[36px] sm:text-[52px] leading-[1.05] max-w-2xl mx-auto">
                Six quiet reasons women choose us.
              </h2>
            </div>
          </Reveal>
          <div className="mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-14">
            {[
              ["01", "One client at a time", "You have the whole hour, and the whole studio, to yourself."],
              ["02", "Private home studio", "Bondi Beach. Discreet, quiet, and away from any clinic feeling."],
              ["03", "Holistic approach to skin", "Skin, sleep, stress and lifestyle — considered as one conversation."],
              ["04", "Nervous system focus", "Slower touch and considered pace, because the body has to soften first."],
              ["05", "Calm & unhurried", "No back-to-back bookings. No music chosen for ambience. Real time held."],
              ["06", "Personalised every visit", "Every appointment is shaped to what your skin and body need that day."],
            ].map(([n, k, v]) => (
              <Reveal key={n} delay={parseInt(n, 10) * 0.05}>
                <div className="border-t border-[#D5CDBF] pt-8" data-testid={`why-${n}`}>
                  <div className="label-eyebrow">{n}</div>
                  <h3 className="serif mt-5 text-[24px] sm:text-[28px] leading-tight">{k}</h3>
                  <p className="mt-4 text-[15px] leading-[1.85] text-[#55524C]">{v}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY WE SLOW DOWN */}
      <section className="py-32 lg:py-44">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-24 items-center">
          <Reveal className="lg:col-span-6 order-2 lg:order-1">
            <div className="label-eyebrow">Why we slow down first</div>
            <h3 className="serif mt-8 text-[32px] sm:text-[44px] leading-tight">
              Skin isn't only about products.
            </h3>
            <div className="mt-10 space-y-6 text-[17px] leading-[1.8] text-[#2E2B27] max-w-lg">
              <p>Today's beauty industry often encourages us to <strong>chase the next trend</strong>, add another serum or follow someone else's routine.</p>
              <p>At Studio A, I take a different approach.</p>
              <p>Rather than overwhelming your skin, I focus on <strong>understanding it</strong>.</p>
              <p>Every treatment is <strong>personalised</strong>, considering not only your skin concerns but also your <strong>stress levels, lifestyle, sleep and nutrition</strong>. Together, we create a routine that <strong>supports your skin</strong> instead of constantly asking it to adapt.</p>
              <p className="pull-quote text-[21px] sm:text-[23px] py-2">The goal isn't perfection. It's healthy, resilient skin that feels balanced and cared for.</p>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6 order-1 lg:order-2 img-zoom">
            <img src={STILLNESS_IMG} alt="Private Studio A treatment room in Bondi Beach, featuring an original painting by Anissa and a calm treatment space." className="w-full aspect-[4/5] object-cover" />
          </Reveal>
        </div>
      </section>

      {/* EDITORIAL BREATH — tea ritual + linen */}
      <section className="py-10 lg:py-16">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-12 gap-6 lg:gap-10 items-end">
          <div className="col-span-7">
            <img src="/images/editorial/linen.jpg" alt="Natural linen in soft morning light." data-testid="home-linen-image" className="w-full aspect-[16/10] object-cover" />
          </div>
          <div className="col-span-5">
            <img src="/images/editorial/tea-ritual.jpg" alt="Herbal tea in a handmade ceramic cup on linen." data-testid="home-tea-image" className="w-full aspect-[4/5] object-cover" />
          </div>
        </div>
      </section>

      {/* FEATURED TREATMENTS (3 only) */}
      <section className="py-32 lg:py-44">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="flex items-end justify-between gap-6 flex-wrap">
              <div>
                <div className="label-eyebrow">The Menu</div>
                <h2 className="serif mt-5 text-[36px] sm:text-[52px] leading-tight">A short, considered list.</h2>
              </div>
              <Link to="/treatments" className="link-underline text-[13px] uppercase tracking-[0.22em]">
                See all
              </Link>
            </div>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-2">
            {treatments.slice(0, 3).map((t, i) => (
              <Reveal key={t.slug} delay={i * 0.08}>
                <Link
                  to={`/treatments/${t.slug}`}
                  data-testid={`featured-treatment-${t.slug}`}
                  className="group block border-t border-[#D5CDBF] py-12"
                >
                  <div className="flex items-baseline justify-between gap-6">
                    <div>
                      <div className="label-eyebrow">{String(i + 1).padStart(2, "0")} · {t.duration}</div>
                      <h3 className="serif mt-5 text-[30px] sm:text-[38px] group-hover:text-[#A96A53] transition-colors">{t.name}</h3>
                      <p className="serif italic mt-2 text-[17px] text-[#55524C]">{t.tagline}</p>
                    </div>
                    <div className="text-[13px] text-[#55524C] whitespace-nowrap">{t.price}</div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI RECOMMENDER */}
      <section className="bg-[#F0EDE6] border-y border-[#D5CDBF]">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-28 lg:py-36">
          <Reveal>
            <div className="label-eyebrow">A quiet question</div>
            <h2 className="serif mt-6 text-[30px] sm:text-[44px] leading-tight max-w-2xl">
              Not sure where to begin? Tell me a little, and I'll suggest a gentle starting point.
            </h2>
          </Reveal>
          <div className="mt-14">
            <TreatmentRecommender />
          </div>
        </div>
      </section>

      {/* FOUNDER — The Story Behind Studio A */}
      <section className="py-32 lg:py-44">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-24 items-start">
          <Reveal className="lg:col-span-5 img-zoom lg:sticky lg:top-28">
            <img src={FOUNDER_IMG} alt="Anissa" className="w-full aspect-[4/5] object-cover" />
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="label-eyebrow">The founder</div>
            <h2 className="serif mt-7 text-[36px] sm:text-[52px] leading-tight">The Story Behind Studio A</h2>
            <p className="serif italic text-[19px] mt-3 text-[#55524C]">Skin Therapist • Founder of Studio A</p>
            <div className="mt-10 space-y-6 text-[17px] leading-[1.85] max-w-xl text-[#2E2B27]">
              <p>Studio A was created from a simple belief:</p>
              <p className="pull-quote text-[22px] sm:text-[24px] py-2">Healthy skin isn't achieved by constantly doing more.</p>
              <p>Today, we're surrounded by trends, complicated routines and endless advice telling us to buy another product or follow the latest beauty ritual. I believe healthy skin begins somewhere else — by <strong>understanding it</strong>.</p>
              <p>Every person who walks through my door has a different story, different skin and different needs. Rather than chasing perfection, I take the time to understand <strong>the whole picture</strong>, looking beyond the surface to consider your <strong>lifestyle, stress levels, sleep, nutrition and daily habits</strong> alongside personalised, evidence-informed skincare.</p>
              <p>I created Studio A as a <strong>private sanctuary</strong> where you can slow down, take a breath and simply be yourself. A place where you feel <strong>supported, listened to</strong> and gently guided through small, sustainable habits that care for both your skin and your wellbeing.</p>
              <p>My treatments combine <strong>advanced skin therapy</strong> with <strong>calming touch, lymphatic massage and intentional care</strong> because I believe our skin often reflects the way we are living.</p>
              <p>When we begin caring for ourselves with more intention — using the right products and the right treatments at the time they are truly needed, instead of simply doing more — the skin can become <strong>healthier, stronger and naturally radiant</strong>.</p>
              <p>My hope is that you leave with healthier skin — but more importantly, feeling <strong>calmer, lighter and more connected to yourself</strong> than when you arrived.</p>
              <p className="serif italic text-[19px]">Because that is what Studio A was created for.</p>
            </div>
            <Link to="/treatments/studio-a-ritual" data-testid="founder-ritual-btn" className="btn-primary mt-12 inline-flex">
              Experience the Studio A Ritual
            </Link>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS — ready for Google Reviews */}
      <section className="bg-[#EAE7E0]/60 py-32 lg:py-40">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <div className="text-center">
              <div className="label-eyebrow">In their words</div>
              <p className="serif italic mt-4 text-[16px] text-[#6E6A63]">From our Google Reviews</p>
            </div>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16">
            {(liveReviews
              ? liveReviews.reviews.slice(0, 3).map((r) => ({ quote: r.text, name: r.author, detail: r.when }))
              : TESTIMONIALS
            ).map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <figure className="border-t border-[#D5CDBF] pt-10">
                  <blockquote className="serif text-[22px] sm:text-[26px] leading-[1.4] text-[#2E2B27]">
                    “{t.quote}”
                  </blockquote>
                  <figcaption className="mt-10 label-eyebrow">{t.name}{t.detail ? ` · ${t.detail}` : ""}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          {!liveReviews && (
            <Reveal>
              <p className="mt-16 text-center text-[13px] text-[#6E6A63] italic">
                A live Google Reviews feed will appear here.
              </p>
            </Reveal>
          )}
        </div>
      </section>

      {/* EDITORIAL BREATH — ocean */}
      <section className="py-10 lg:py-16">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <img src="/images/editorial/ocean.jpg" alt="Calm, muted ocean water at dawn near Bondi Beach." data-testid="home-ocean-image" className="w-full aspect-[21/9] object-cover" />
          <p className="pull-quote text-center mt-10 text-[22px] sm:text-[26px] max-w-2xl mx-auto">Minutes from the ocean. A world away from the noise.</p>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="py-32 lg:py-44">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 text-center">
          <Reveal>
            <div className="label-eyebrow">Begin</div>
            <h2 className="serif mt-8 text-[40px] sm:text-[64px] leading-[1.05]">
              When you're ready.
            </h2>
            <p className="mt-8 max-w-xl mx-auto text-[17px] leading-[1.8] text-[#55524C]">
              By appointment only. One woman at a time, in Bondi Beach.
            </p>
            <Link
              to="/book"
              data-testid="cta-book-btn"
              className="btn-primary mt-14 inline-flex"
            >
              Begin Your Ritual <ArrowRight size={14} strokeWidth={1.6} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
